// samvad-platform/admin-frontend/src/auth/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi, setUnauthorizedHandler } from '../api/client';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await authApi.me();
      // Assume the backend returns { user: { ...perms } } or similar
      setUser(data || null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    setUnauthorizedHandler(() => {
      setUser(null);
    });
  }, [refresh]);

  const logout = useCallback(async () => {
    setLoggingOut(true);
    try {
      const data = await authApi.logout();
      setUser(null);
      if (data?.ssoLogoutUrl) {
        // Full-page navigation to the IdP's logout endpoint is already
        // underway — don't clear loggingOut, the page is about to unload.
        window.location.href = data.ssoLogoutUrl;
        return true; // signal: caller should NOT also client-side navigate
      }
    } catch {
      /* noop */
    }
    setUser(null);
    setLoggingOut(false);
    return false; // no SSO redirect happened, caller should navigate itself
  }, []);

  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    // Match the backend's permission structure
    const perms = user.permissions || [];
    if (perms.includes('*')) return true;
    return perms.includes(perm);
  }, [user]);

  return (
    <AuthContext.Provider
      value={{ user, loading, loggingOut, refresh, logout, hasPermission }}
    >
      {children}
    </AuthContext.Provider>
  );
}